import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Flame, Clock, Award, FileSpreadsheet, FileText } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/molecules/Card';
import { MetricCard } from '@/components/molecules/MetricCard';
import { Button } from '@/components/atoms/Button';
import { IconButton } from '@/components/atoms/IconButton';
import { Badge } from '@/components/atoms/Badge';
import { Avatar } from '@/components/atoms/Avatar';
import { Separator } from '@/components/atoms/Separator';
import { Spinner } from '@/components/atoms/Spinner';
import { Select } from '@/components/atoms/Select';
import { Input } from '@/components/atoms/Input';
import { DataTable } from '@/components/organisms/DataTable';
import type { DataTableColumn } from '@/components/organisms/DataTable';
import { ROUTES } from '@/constants/routes';
import { useEmployee, useEmployeeProgress, useSetEmployeeActive } from '@/hooks/useCompanyData';
import { downloadEmployeeReport } from '@/services/api/company.api';
import { formatTime } from '@/utils/formatTime';
import type { EmployeeStoryHistory } from '@/types/company.types';
import styles from './EmployeeDetailPage.module.scss';

type ReportPeriod = 'current_month' | 'last_month' | 'custom';

export function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { employee, isLoading } = useEmployee(id);
  const { progress } = useEmployeeProgress(id);
  const { setEmployeeActive, isSettingActive } = useSetEmployeeActive();
  const [active, setActive] = useState<boolean | null>(null);

  useEffect(() => {
    if (employee) setActive(employee.isActive);
  }, [employee]);

  const handleToggleActive = async () => {
    if (!id || !employee) return;
    const current = active ?? employee.isActive;
    const updated = await setEmployeeActive(id, !current);
    if (updated) setActive(updated.isActive);
  };
  const [reportPeriod, setReportPeriod] = useState<ReportPeriod>('current_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);

  useEffect(() => {
    const now = new Date();
    if (reportPeriod === 'current_month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setStartDate(start.toISOString().split('T')[0] ?? '');
      setEndDate(end.toISOString().split('T')[0] ?? '');
    } else if (reportPeriod === 'last_month') {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      setStartDate(start.toISOString().split('T')[0] ?? '');
      setEndDate(end.toISOString().split('T')[0] ?? '');
    }
  }, [reportPeriod]);

  const storyHistory: EmployeeStoryHistory[] = progress?.storyHistory ?? [];

  const handleDownloadExcel = async () => {
    if (!id) return;
    try {
      setIsDownloadingExcel(true);
      await downloadEmployeeReport(id, 'xlsx', startDate, endDate);
    } catch (error) {
      console.error('Failed to download Excel report:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to download Excel report: ${errorMessage}`);
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!id) return;
    try {
      setIsDownloadingPDF(true);
      await downloadEmployeeReport(id, 'pdf', startDate, endDate);
    } catch (error) {
      console.error('Failed to download PDF report:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to download PDF report: ${errorMessage}`);
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  const challengeColumns: DataTableColumn<EmployeeStoryHistory>[] = [
    {
      key: 'title',
      header: 'Story',
      accessor: (row) => row.storyTitle,
      sortable: true,
      width: '35%',
    },
    {
      key: 'level',
      header: 'Level',
      accessor: (row) => <Badge variant="default">{row.level}</Badge>,
      sortable: true,
      align: 'center',
    },
    {
      key: 'score',
      header: 'Score',
      accessor: (row) => `${row.score}%`,
      sortable: true,
      align: 'center',
    },
    {
      key: 'xp',
      header: 'XP Earned',
      accessor: (row) => row.xpEarned,
      sortable: true,
      align: 'center',
    },
    {
      key: 'time',
      header: 'Time Spent',
      accessor: (row) => formatTime(row.timeSpent),
      sortable: true,
      align: 'center',
    },
    {
      key: 'completed',
      header: 'Completed',
      accessor: (row) => new Date(row.completedAt || '').toLocaleDateString(),
      sortable: true,
      align: 'right',
    },
  ];

  if (isLoading) {
    return (
      <div className={styles.employeeDetail}>
        <div className={styles.employeeDetail__loading}>
          <Spinner size="lg" />
          <p>Loading employee data...</p>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className={styles.employeeDetail}>
        <p>Employee not found.</p>
      </div>
    );
  }

  return (
    <div className={styles.employeeDetail}>
      <div className={styles.employeeDetail__topbar}>
        <IconButton
          icon={ArrowLeft}
          onClick={() => navigate(ROUTES.COMPANY.EMPLOYEES)}
          aria-label="Volver a Empleados"
        />
        {employee.role === 'student' && (
          <Button
            variant={(active ?? employee.isActive) ? 'destructive' : 'primary'}
            onClick={handleToggleActive}
            isLoading={isSettingActive}
          >
            {(active ?? employee.isActive) ? 'Desactivar' : 'Activar'}
          </Button>
        )}
      </div>

      <Card className={styles.employeeDetail__header}>
        <CardContent>
          <div className={styles.employeeDetail__profile}>
            <Avatar
              src={employee.avatar}
              alt={employee.name}
              fallback={employee.name}
              size="xl"
            />
            <div className={styles.employeeDetail__profileInfo}>
              <div className={styles.employeeDetail__profileMain}>
                <h1 className={styles.employeeDetail__name}>{employee.name}</h1>
                {employee.role === 'student' ? (
                  <Badge variant="default" className={styles.employeeDetail__levelBadge}>
                    {employee.level}
                  </Badge>
                ) : (
                  <Badge variant="info">Admin</Badge>
                )}
                <Badge variant={(active ?? employee.isActive) ? 'success' : 'error'}>
                  {(active ?? employee.isActive) ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <p className={styles.employeeDetail__email}>{employee.email}</p>
              {employee.department && employee.position && (
                <p className={styles.employeeDetail__position}>
                  {employee.position} • {employee.department}
                </p>
              )}
              <div className={styles.employeeDetail__dates}>
                <span>Joined: {employee.registeredAt ? new Date(employee.registeredAt).toLocaleDateString() : '—'}</span>
                <Separator orientation="vertical" className={styles.employeeDetail__dateSeparator} />
                <span>Last active: {employee.lastActivityAt ? new Date(employee.lastActivityAt).toLocaleDateString() : 'Never'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Download Employee Report</CardTitle>
        </CardHeader>
        <CardContent>
          <div className={styles.employeeDetail__reportConfig}>
            <div className={styles.employeeDetail__configGroup}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className={styles.employeeDetail__label}>Report Period</label>
              <Select
                value={reportPeriod}
                onValueChange={(value) => setReportPeriod(value as ReportPeriod)}
                options={[
                  { label: 'Current Month', value: 'current_month' },
                  { label: 'Last Month', value: 'last_month' },
                  { label: 'Custom Period', value: 'custom' },
                ]}
              />
            </div>

            {reportPeriod === 'custom' && (
              <div className={styles.employeeDetail__dateRange}>
                <div className={styles.employeeDetail__configGroup}>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className={styles.employeeDetail__label}>Start Date</label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className={styles.employeeDetail__configGroup}>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className={styles.employeeDetail__label}>End Date</label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}

            <div className={styles.employeeDetail__downloadActions}>
              <Button
                variant="outline"
                size="md"
                onClick={handleDownloadExcel}
                disabled={isDownloadingExcel || isLoading}
              >
                <FileSpreadsheet size={16} />
                {isDownloadingExcel ? 'Downloading...' : 'Download Excel'}
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleDownloadPDF}
                disabled={isDownloadingPDF || isLoading}
              >
                <FileText size={16} />
                {isDownloadingPDF ? 'Downloading...' : 'Download PDF'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className={styles.employeeDetail__stats}>
        <MetricCard
          icon={Trophy}
          label="Total XP"
          value={employee.stats.totalXP.toLocaleString()}
          description={`Rank #${employee.stats.rank} in company`}
        />
        <MetricCard
          icon={Award}
          label="Stories Completed"
          value={employee.stats.storiesCompleted.toString()}
          description={`${employee.stats.averageScore}% average score`}
        />
        <MetricCard
          icon={Flame}
          label="Current Streak"
          value={`${employee.stats.currentStreak} days`}
          description={`Best: ${employee.stats.longestStreak} days`}
        />
        <MetricCard
          icon={Clock}
          label="Total Time"
          value={formatTime(employee.stats.totalTimeSpent)}
          description="Time spent learning"
        />
      </div>

      <Card className={styles.employeeDetail__history}>
        <CardHeader>
          <CardTitle>Story History</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={challengeColumns}
            data={storyHistory}
            emptyMessage="No stories completed yet"
            pageSize={10}
          />
        </CardContent>
      </Card>
    </div>
  );
}

export default EmployeeDetailPage;
